import { CustomBumperConfig, Recipe } from './types'
import { getColor } from '@/js/pages/product/custom/constants'

export const mapConfigToRecipe = (config: CustomBumperConfig): Recipe | void => {
  const recipe: Recipe = {
    sku       : config.sku,
    productId : config.productId,
    stencils  : config.stencils?.map(stencil => ({
      key  : 'custom-bumper-stencil',
      path : stencil,
      href : '',
    })) || [],
    renders: config.renders?.map(render => ({
      key  : 'custom-bumper-render',
      path : render,
      href : '',
    })) || [],
    customizations: [],
  }

  switch (config.type) {
    case 'file':
      recipe.customizations = [
        {
          key   : 'custom-bumper-color',
          type  : 'color',
          value : config.color!,
        },
        {
          key   : 'custom-bumper-image',
          type  : 'image',
          value : config.image!,
        },
      ]
      break
    case 'design':
      recipe.customizations = [
        {
          key   : 'custom-bumper-color',
          type  : 'color',
          value : config.color!,
        },
        {
          key   : 'custom-bumper-design',
          type  : 'image',
          value : config.design!,
          name  : config.designLabel,
        },
      ]
      break
    case 'text':
      recipe.customizations = [
        {
          key   : 'custom-bumper-color',
          type  : 'color',
          value : config.color!,
        },
        {
          key   : 'custom-bumper-font',
          type  : 'font',
          value : config.font!,
        },
        {
          key   : 'custom-bumper-text',
          type  : 'text',
          value : config.text!,
          size  : config.fontSize!,
          font  : config.font!,
        },
      ]
      break
  }

  return recipe
}

export const mapRecipeToConfig = (recipe: Recipe): CustomBumperConfig => {
  const config: CustomBumperConfig = {
    sku       : recipe.sku,
    productId : recipe.productId,
    text      : '',
    font      : 'arvo',
  }

  recipe.customizations.forEach((customization) => {
    if (customization.type === 'image') {
      if (customization.key === 'custom-bumper-design') {
        config.design = customization.value
        config.type = 'design'
      } else {
        config.image = customization.value
        config.type = 'file'
      }
    }
    if (customization.type === 'text') {
      config.text = customization.value
      config.font = customization.font
      config.fontSize = customization.size
      config.type = 'text'
    }
    if (customization.type === 'color') {
      config.color = getColor(customization.value)
    }
  })

  return config
}

export const getExtension = (filename: string) => {
  const parts = filename.split('.')

  return parts[parts.length - 1]
}

export const isSVG = (file: File): boolean => (file.name ? getExtension(file.name) === 'svg' : file.type.indexOf('svg') > -1)

export const isImage = (filename: string) => {
  const ext = getExtension(filename)
  switch (ext.toLowerCase()) {
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'bmp':
    case 'png':
      // etc
      return true
    default:
      return false
  }
}

// https://stackoverflow.com/questions/12168909/blob-from-dataurl
export const dataURItoBlob = (dataURI: string) => {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  const byteString = atob(dataURI.split(',')[1])

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length)

  // create a view into the buffer
  const ia = new Uint8Array(ab)

  // set the bytes of the buffer to the correct values
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i)
  }

  // write the ArrayBuffer to a blob, and you're done
  return new Blob([ab], { type: mimeString })
}

const SAVED_UPLOADS_KEY = 'custom-bumper-uploads'

const getCachedFileStrings = (cacheKey: string): {file: string, trace?: string, fileName: string, fileSize: number}[] => {
  try {
    const cachedString = window.localStorage.getItem(cacheKey)

    return cachedString ? JSON.parse(cachedString) : []
  } catch (e) {
    // Error loading from localstorage
    return []
  }
}

export const buildFileOptions = (cacheKey = SAVED_UPLOADS_KEY) => {
  const cachedFileStrings = getCachedFileStrings(cacheKey)

  return cachedFileStrings.filter(s => !!s.file).map(({ file, trace, fileName, fileSize }) => {
    const blob = dataURItoBlob(file)

    return { file: blob, fileName, fileSize, trace }
  })
}

export const clearCache = (cacheKey = SAVED_UPLOADS_KEY) => {
  window.localStorage.removeItem(cacheKey)
}

export const cacheFileOption = (file: File, tracedImage?: string, cacheKey = SAVED_UPLOADS_KEY): void => {
  const cachedFileStrings = getCachedFileStrings(cacheKey)
  const reader = new FileReader()
  reader.addEventListener('load', () => {
    if (reader.result) {
      cachedFileStrings.push({ file: reader.result.toString(), fileName: file.name, fileSize: file.size, trace: tracedImage })
      try {
        window.localStorage.setItem(cacheKey, JSON.stringify(cachedFileStrings))
      } catch (e) {
        // Error saving to local storage - maybe image was too big?
      }
    }
  })
  reader.readAsDataURL(file)
}

const loadImage = (url: string): Promise<HTMLImageElement> => new Promise(resolve => {
  const image = new Image()
  image.onload = () => {
    resolve(image)
  }
  image.src = url
})

const loadSvg = (svg: Blob): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image()
  image.onload = () => setTimeout(() => resolve(image), 100)
  image.onerror = reject
  image.src = URL.createObjectURL(svg)
})

export const renderToCanvas = async (
  stencilHtml: string,
  renderImgSrc: string,
  renderDimensions: { width: number, height: number },
  stencilDimensions: { width: number, height: number, x: number, y: number }): Promise<HTMLCanvasElement | null> => {
  const svgBlob = new Blob([stencilHtml], { type: 'image/svg+xml' })

  const canvas = document.createElement('canvas')
  canvas.width = renderDimensions.width
  canvas.height = renderDimensions.height
  const ctx = canvas.getContext('2d')

  if (ctx) {
    try {
      const renderImg = await loadImage(renderImgSrc)
      ctx.drawImage(renderImg, 0, 0, renderDimensions.width, renderDimensions.height)
      if (stencilHtml) {
        const stencilImg = await loadSvg(svgBlob)
        ctx.drawImage(stencilImg, stencilDimensions.x, stencilDimensions.y, stencilDimensions.width, stencilDimensions.height)
      }

      return canvas
    } catch (e) {
      console.error('Error rendering to canvas', e)
    }
  }

  return null
}

type SVGDimensions = {
  height?: number,
  width?: number
}
export const getSVGDimensions = (svgContent: string): SVGDimensions => {
  const regex = /viewBox="(.*?)"/
  const match = regex.exec(svgContent)
  if (match) {
    const group = match[1]
    const viewBox = group.split(' ').filter(d => d !== '0')

    return {
      height : +viewBox[1],
      width  : +viewBox[0],
    }
  }

  return { height: undefined, width: undefined }
}

export const lightOrDark = (color: string): string => {
  const updatedColor = +(`0x${color.slice(1).replace(
    /./g, '$&$&'
  )}`)

  const r = updatedColor >> 16
  const g = updatedColor >> 8 & 255
  const b = updatedColor & 255

  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(
    0.299 * (r * r)
    + 0.587 * (g * g)
    + 0.114 * (b * b)
  )

  // Using the HSP value, determine whether the color is light or dark
  if (hsp > 127.5) {
    return 'light'
  }

  return 'dark'
}

// https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript/8831937#8831937
export const hashCode = (str: string) => {
  let hash = 0
  for (let i = 0, len = str.length; i < len; i += 1) {
    const chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }

  return hash
}
